"use client";

import logger from "@/utils/logger";
// components/layout/Header.tsx

import React, { useState, useEffect, useRef, useMemo } from "react";
import Link from "next/link";
import {
    Navbar,
    Container,
    NavDropdown,
    Image,
    Spinner,
    Nav,
    Button,
} from "react-bootstrap";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "@/config/msal-config";
import { useTheme } from "next-themes";
import { MobileNavMenu } from "../menu/MobileNavMenu";
import { MultiLevelNavDropdownMenu } from "../menu/MultiLevelDropdownMenu";
import NotificationPopover from "./NotificationPopover";
import { getInitials } from "@/utils/getInitials";
import { BsTicketPerforatedFill } from "react-icons/bs";
import { useGetNavigationListByUser } from "@/services/hooks/navigationHooks";
import LogoutConfirmationModal from "@/components/ui/LogoutConfirmationModal";
import { useGlobalLogout } from "@/hooks/useGlobalLogout";
import { UserApiService } from "@/services/api/internal-login/internal-user-api-service";
import { clearLocalStoragePreservingDeviceId } from "@/services/deviceId";
import { compressProfilePhoto } from "@/utils/photoCompression"; // Import the new hook
import { useRecoverableProtectedAuth } from "@/hooks/useRecoverableProtectedAuth";
import { useOptionalPermissions } from "@/contexts/PermissionContext";
import { MenuRoute, MenuRouteItem } from "../menu/types";

const DRIVER_MAP_DISPLAY_PERMISSIONS = ["DISV", "DISPLAY_VIEW"];
const DRIVER_MAP_MENU_LABEL = "Live Map";
const DRIVER_MAP_MENU_ROUTE = "/display/driver-map";

// Same permissions that gate the Trip Advisor entry point on the Trips list.
const RECOMMEND_TRIPS_PERMISSIONS = ["CTC", "CREATE_TRIP_CREATE"];
const RECOMMEND_TRIPS_MENU_LABEL = "Recommend Trips";
const RECOMMEND_TRIPS_MENU_ROUTE = "/trip-advisor";
const TRIP_CREATE_MENU_ROUTE = "/trip/create";

const menuContainsRoute = (
    items: Array<MenuRouteItem | MenuRoute> | null | undefined,
    route: string
): boolean => {
    if (!items) {
        return false;
    }

    return items.some((item) => {
        if (item.to === route) {
            return true;
        }

        return menuContainsRoute(item.children ?? [], route);
    });
};

const isDisplayMenuBranch = (item: MenuRouteItem | MenuRoute): boolean => {
    if (typeof item.to === "string" && item.to.startsWith("/display")) {
        return true;
    }

    return (item.children ?? []).some((child) => isDisplayMenuBranch(child));
};

// "/trip" or "/trip/..." only — must not match "/trip-advisor" itself.
const isTripsMenuBranch = (item: MenuRouteItem | MenuRoute): boolean => {
    if (
        typeof item.to === "string" &&
        (item.to === "/trip" || item.to.startsWith("/trip/"))
    ) {
        return true;
    }

    return (item.children ?? []).some((child) => isTripsMenuBranch(child));
};

const cloneMenuRoute = (item: MenuRoute): MenuRoute => ({
    ...item,
    children: item.children?.map((child) => cloneMenuRoute(child)) ?? null,
});

const cloneMenuRouteItem = (item: MenuRouteItem): MenuRouteItem => ({
    ...item,
    children: item.children?.map((child) => cloneMenuRoute(child)) ?? null,
});

// Type definitions for Window extensions (keeping existing ones)
interface WindowWithExtensions extends Window {
    userCache?: Record<string, unknown>;
    authCache?: Record<string, unknown>;
    apiCache?: Record<string, unknown>;
    gc?: () => void;
}

interface WindowWithWebSQL extends Window {
    openDatabase?: (
        name: string,
        version: string,
        displayName: string,
        estimatedSize: string
    ) => {
        transaction: (callback: (tx: WebSQLTransaction) => void) => void;
    };
}

interface WebSQLTransaction {
    executeSql: (sql: string, params?: unknown[]) => void;
}

const Header = () => {
    const {
        isAuthenticated: stableIsAuthenticated,
        currentUser: stableCurrentUser,
        loginType: stableLoginType,
    } = useRecoverableProtectedAuth();

    // 🔥 NEW: Use the global logout hook
    const { triggerGlobalLogout } = useGlobalLogout();

    const [profilePic, setProfilePic] = useState<string | null>(null);
    const [picLoading, setPicLoading] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const { theme, setTheme, resolvedTheme } = useTheme();
    const permissionContext = useOptionalPermissions();

    // UserApiService instance for profile photo operations
    const [userApiService] = useState(() => new UserApiService());

    // 🔥 FIX: Track if photo has been fetched to prevent duplicate calls
    const photoFetchedRef = useRef<number | null>(null);

    const msalCurrentUser =
        stableLoginType === "msal" &&
        stableCurrentUser &&
        "firstName" in stableCurrentUser
            ? stableCurrentUser
            : null;
    const userName = msalCurrentUser
        ? msalCurrentUser.firstName ||
          msalCurrentUser.name ||
          msalCurrentUser.displayName
        : stableCurrentUser?.displayName ||
          stableCurrentUser?.name ||
          stableCurrentUser?.email;
    const organizationName = stableCurrentUser?.organization;

    // Determine where the logo should navigate based on login type
    const getLogoHref = () => {
        if (!stableIsAuthenticated) {
            return "/"; // Not authenticated → go to login page
        }

        if (stableLoginType === "external") {
            return "/externalLoginDashboard"; // External user → external dashboard
        } else if (stableLoginType === "msal") {
            return "/dashboard"; // MSAL user → regular dashboard
        }

        return "/"; // Fallback to login page
    };

    const getLogoSrc = () => {
        if (stableLoginType === "external") {
            return "/logo-dynamic.png";
        }
        return resolvedTheme === "dark"
            ? "/logo_horz_dark.png"
            : "/logo_horz.png";
    };

    const { data: navigationMenus } = useGetNavigationListByUser(
        stableCurrentUser?.userId || 0
    );

    const augmentedNavigationMenus = useMemo(() => {
        const sourceMenus = navigationMenus ?? [];
        let menus = sourceMenus.map((menu) => cloneMenuRouteItem(menu));

        // Live Map under the Display menu (appended last, or top-level if no
        // Display branch exists).
        if (
            permissionContext?.hasAnyPermission(
                DRIVER_MAP_DISPLAY_PERMISSIONS
            ) &&
            !menuContainsRoute(menus, DRIVER_MAP_MENU_ROUTE)
        ) {
            const driverMapChild: MenuRoute = {
                name: DRIVER_MAP_MENU_LABEL,
                to: DRIVER_MAP_MENU_ROUTE,
                openInNewTab: true,
            };

            const displayMenu = menus.find((menu) => isDisplayMenuBranch(menu));
            if (displayMenu) {
                displayMenu.children = [
                    ...(displayMenu.children ?? []),
                    driverMapChild,
                ];
            } else {
                menus = [
                    ...menus,
                    {
                        label: DRIVER_MAP_MENU_LABEL,
                        to: DRIVER_MAP_MENU_ROUTE,
                        children: null,
                        // Story 31457 AC: Live Map always opens in a new tab, including
                        // this no-Display-branch fallback.
                        openInNewTab: true,
                    },
                ];
            }
        }

        // Recommend Trips under the Trips menu, right after Create Standard
        // Trip. Users without a Trips branch have no trip access, so there is
        // no top-level fallback.
        if (
            permissionContext?.hasAnyPermission(RECOMMEND_TRIPS_PERMISSIONS) &&
            !menuContainsRoute(menus, RECOMMEND_TRIPS_MENU_ROUTE)
        ) {
            const tripsMenu = menus.find((menu) => isTripsMenuBranch(menu));
            if (tripsMenu) {
                const children = [...(tripsMenu.children ?? [])];
                const createIndex = children.findIndex(
                    (child) => child.to === TRIP_CREATE_MENU_ROUTE
                );
                const insertAt =
                    createIndex >= 0 ? createIndex + 1 : children.length;
                children.splice(insertAt, 0, {
                    name: RECOMMEND_TRIPS_MENU_LABEL,
                    to: RECOMMEND_TRIPS_MENU_ROUTE,
                });
                tripsMenu.children = children;
            }
        }

        return menus;
    }, [navigationMenus, permissionContext]);

    // 🔥 ENHANCED: Complete cache and session clearing function (keeping existing)
    const clearEverythingOnLogout = async () => {
        logger.info("🧹 Starting COMPLETE logout cleanup...");

        try {
            // Clear profile picture URL to prevent memory leaks
            if (profilePic) {
                URL.revokeObjectURL(profilePic);
                setProfilePic(null);
            }

            // Preserve the browser-scoped device identifier across sign-outs.
            logger.info(
                "🗑️ Clearing localStorage while preserving deviceId..."
            );
            clearLocalStoragePreservingDeviceId();

            // Clear ALL sessionStorage
            logger.info("🗑️ Clearing ALL sessionStorage...");
            sessionStorage.clear();

            // Enhanced MSAL cache clearing
            if (stableLoginType === "msal") {
                try {
                    logger.info("🗑️ Clearing MSAL cache...");
                    const msalInstance = new PublicClientApplication(
                        msalConfig
                    );
                    await msalInstance.initialize();
                    const accounts = msalInstance.getAllAccounts();
                    if (accounts.length > 0) {
                        logger.info(
                            `Found ${accounts.length} MSAL accounts - they will be cleared by storage clearing`
                        );
                    }
                    logger.info("✅ MSAL cache completely cleared");
                } catch (msalError) {
                    console.error("❌ MSAL cleanup error:", msalError);
                }
            }

            // Clear browser caches
            if ("caches" in window) {
                try {
                    logger.info("🗑️ Clearing browser caches...");
                    const cacheNames = await caches.keys();
                    await Promise.all(
                        cacheNames.map(async (cacheName) => {
                            try {
                                await caches.delete(cacheName);
                                logger.info(`✅ Deleted cache: ${cacheName}`);
                            } catch (cacheError) {
                                console.warn(
                                    `⚠️ Failed to delete cache ${cacheName}:`,
                                    cacheError
                                );
                            }
                        })
                    );
                    logger.info("✅ All browser caches cleared");
                } catch (cacheError) {
                    console.error("❌ Cache cleanup error:", cacheError);
                }
            }

            // Clear IndexedDB databases
            if ("indexedDB" in window) {
                try {
                    logger.info("🗑️ Clearing IndexedDB...");
                    const commonDbNames = [
                        "dispatch-iq",
                        "msal",
                        "authentication",
                        "user-data",
                        "app-cache",
                        "localforage",
                        "keyval-store",
                    ];

                    for (const dbName of commonDbNames) {
                        try {
                            const deleteRequest =
                                indexedDB.deleteDatabase(dbName);
                            await new Promise<void>((resolve, reject) => {
                                deleteRequest.onsuccess = () => resolve();
                                deleteRequest.onerror = () =>
                                    reject(deleteRequest.error);
                                deleteRequest.onblocked = () => {
                                    console.warn(
                                        `IndexedDB delete blocked for: ${dbName}`
                                    );
                                    resolve();
                                };
                            });
                            logger.info(`✅ Deleted IndexedDB: ${dbName}`);
                        } catch {
                            logger.info(
                                `ℹ️ IndexedDB ${dbName} didn't exist or couldn't be deleted`
                            );
                        }
                    }
                    logger.info("✅ IndexedDB cleanup completed");
                } catch (indexedDbError) {
                    console.error(
                        "❌ IndexedDB cleanup error:",
                        indexedDbError
                    );
                }
            }

            // Clear cookies
            try {
                logger.info("🗑️ Clearing cookies...");
                document.cookie.split(";").forEach((cookie) => {
                    const eqPos = cookie.indexOf("=");
                    const name =
                        eqPos > -1
                            ? cookie.substr(0, eqPos).trim()
                            : cookie.trim();
                    if (name) {
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
                        const domain = window.location.hostname
                            .split(".")
                            .slice(-2)
                            .join(".");
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain}`;
                    }
                });
                logger.info("✅ Cookies cleared");
            } catch (cookieError) {
                console.error("❌ Cookie cleanup error:", cookieError);
            }

            // Clear service worker caches
            if ("serviceWorker" in navigator) {
                try {
                    logger.info("🗑️ Clearing service worker caches...");
                    const registrations =
                        await navigator.serviceWorker.getRegistrations();
                    for (const registration of registrations) {
                        await registration.unregister();
                        logger.info("✅ Unregistered service worker");
                    }
                } catch (swError) {
                    console.error("❌ Service worker cleanup error:", swError);
                }
            }

            // Clear Web SQL databases
            try {
                logger.info("🗑️ Clearing Web SQL...");
                const windowWithWebSQL = window as WindowWithWebSQL;
                if (windowWithWebSQL.openDatabase) {
                    const db = windowWithWebSQL.openDatabase("", "", "", "");
                    if (db) {
                        db.transaction((tx: WebSQLTransaction) => {
                            tx.executeSql("DROP TABLE IF EXISTS data");
                        });
                    }
                }
                logger.info("✅ Web SQL cleared");
            } catch {
                logger.info("ℹ️ Web SQL not available or already clear");
            }

            // Clear memory caches
            try {
                logger.info("🗑️ Clearing memory caches...");
                const windowWithCache = window as WindowWithExtensions;
                if (windowWithCache.userCache) delete windowWithCache.userCache;
                if (windowWithCache.authCache) delete windowWithCache.authCache;
                if (windowWithCache.apiCache) delete windowWithCache.apiCache;
                if (windowWithCache.gc) {
                    windowWithCache.gc();
                    logger.info("✅ Forced garbage collection");
                }
                logger.info("✅ Memory caches cleared");
            } catch {
                logger.info("ℹ️ Memory cache clearing completed");
            }

            logger.info("🎉 COMPLETE logout cleanup finished successfully!");
        } catch (error) {
            console.error("❌ Error during complete logout cleanup:", error);
        }
    };

    // Logout handlers
    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    // 🔥 UPDATED: Use global logout instead of regular logout
    const handleLogoutConfirm = async () => {
        setShowLogoutModal(false);

        try {
            logger.info("🚀 Starting global logout process...");

            // Clear everything first
            await clearEverythingOnLogout();

            // 🔥 NEW: Trigger global logout across all tabs
            await triggerGlobalLogout();
        } catch (error) {
            console.error("❌ Critical error during global logout:", error);

            // LAST RESORT: Force nuclear option
            logger.info("🚨 Executing last resort logout...");
            clearLocalStoragePreservingDeviceId();
            sessionStorage.clear();
            window.location.replace("/");

            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    };

    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    // Fetch user profile photo - check DB first, then Graph API if needed
    useEffect(() => {
        const fetchProfilePhoto = async () => {
            if (
                !msalCurrentUser ||
                !stableIsAuthenticated ||
                stableLoginType !== "msal"
            ) {
                setProfilePic(null);
                return;
            }

            // 🔥 FIX: Skip if already fetched for this user
            if (photoFetchedRef.current === msalCurrentUser.userId) {
                logger.info(
                    "📸 Photo already fetched for user:",
                    msalCurrentUser.userId
                );
                return;
            }

            setPicLoading(true);
            photoFetchedRef.current = msalCurrentUser.userId;

            try {
                // STEP 1: Check if photo exists in database
                const hasPhoto = await userApiService.checkProfilePhotoExists(
                    msalCurrentUser.userId
                );

                if (hasPhoto) {
                    // STEP 2: Get photo from database
                    logger.info("📸 Profile photo found in DB, loading...");
                    const dbPhoto = await userApiService.getProfilePhoto(
                        msalCurrentUser.userId
                    );
                    if (dbPhoto) {
                        setProfilePic(dbPhoto);
                        // Also update the user profile state to include the photo
                        if (msalCurrentUser) {
                            // This will be handled by auth context in the future
                        }
                        return;
                    }
                }

                // STEP 3: No photo in DB, fetch from Microsoft Graph API
                logger.info("📸 No photo in DB, fetching from Graph API...");
                const msalInstance = new PublicClientApplication(msalConfig);
                await msalInstance.initialize();

                const accounts = msalInstance.getAllAccounts();
                if (accounts.length === 0) {
                    logger.info("📸 No MSAL accounts found");
                    return;
                }

                const account = accounts[0];
                const response = await msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account: account,
                });

                const photoResponse = await fetch(
                    "https://graph.microsoft.com/v1.0/me/photo/$value",
                    {
                        headers: {
                            Authorization: `Bearer ${response.accessToken}`,
                        },
                    }
                );

                if (photoResponse.ok) {
                    const photoBlob = await photoResponse.blob();

                    // STEP 4: Compress the photo to 64x64
                    logger.info("📸 Compressing photo to 64x64...");
                    const compressedBase64 =
                        await compressProfilePhoto(photoBlob);

                    // STEP 5: Save compressed photo to database
                    logger.info("📸 Saving compressed photo to DB...");
                    await userApiService.updateProfilePhoto(
                        msalCurrentUser.userId,
                        compressedBase64
                    );

                    // STEP 6: Set the photo in state
                    setProfilePic(compressedBase64);

                    logger.info(
                        "📸 Profile photo successfully fetched, compressed, and saved!"
                    );
                } else {
                    logger.info("📸 No photo available in Graph API");
                }
            } catch (error) {
                console.error("📸 Error in profile photo flow:", error);
            } finally {
                setPicLoading(false);
            }
        };

        fetchProfilePhoto();
    }, [
        msalCurrentUser,
        stableIsAuthenticated,
        stableLoginType,
        userApiService,
    ]);

    // Cleanup profile picture URL on unmount
    useEffect(() => {
        return () => {
            if (profilePic) {
                URL.revokeObjectURL(profilePic);
            }
        };
    }, [profilePic]);

    return (
        <>
            <Navbar expand="lg" className="header-navbar" variant="dark">
                <Container fluid className="px-3">
                    <div className="d-flex flex-column w-100">
                        <div className="d-flex align-items-center w-100 justify-content-between">
                            <div className="d-flex align-items-center flex-grow-1">
                                <Navbar.Brand
                                    as={Link}
                                    href={getLogoHref()}
                                    className="brand me-4"
                                >
                                    <div
                                        className="d-flex flex-column align-items-center"
                                        style={{ position: "relative" }}
                                    >
                                        <Image
                                            src={getLogoSrc()}
                                            //src="/logo-dynamic.png"
                                            alt="Dispatch-IQ"
                                            data-testid="app-logo"
                                            height={60}
                                            loading="eager"
                                            style={{ objectFit: "contain" }}
                                        />
                                        {stableLoginType === "external" &&
                                            organizationName && (
                                                <span
                                                    style={{
                                                        fontSize: "12pt",
                                                        color: "#0039BF",
                                                        lineHeight: 1.1,
                                                        position: "absolute",
                                                        top: 36,
                                                        left: 0,
                                                        width: "100%",
                                                        textAlign: "left",
                                                        paddingLeft: "70px",
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    {organizationName}
                                                </span>
                                            )}
                                    </div>
                                </Navbar.Brand>
                                <div className="d-lg-none ms-auto">
                                    <MobileNavMenu
                                        menuItems={augmentedNavigationMenus}
                                        theme={theme}
                                        setTheme={setTheme}
                                        profilePic={profilePic}
                                        picLoading={picLoading}
                                        onLogoutClick={handleLogoutClick}
                                    />
                                </div>
                                <Nav className="header-nav d-none d-lg-flex menu-no-theme">
                                    {stableIsAuthenticated && (
                                        <>
                                            {augmentedNavigationMenus.map(
                                                (item, index) => (
                                                    <MultiLevelNavDropdownMenu
                                                        key={index}
                                                        menu={item}
                                                    />
                                                )
                                            )}
                                        </>
                                    )}
                                </Nav>
                            </div>
                            <div className="d-none d-lg-flex align-items-center ms-auto">
                                {/* ServiceNow Icon - desktop only */}
                                <div className="theme-ticket-wrapper me-2">
                                    <Button
                                        variant="link"
                                        href="https://support.nybce.org/"
                                        target="_blank"
                                        className="p-0 me-3 position-relative"
                                        aria-label="Create a ticket"
                                    >
                                        <BsTicketPerforatedFill
                                            size={24}
                                            className="mx-1 secondary-color-text"
                                            style={{ rotate: "145deg" }}
                                        />
                                    </Button>
                                </div>
                                <NotificationPopover />
                                {stableIsAuthenticated && stableCurrentUser ? (
                                    <div className="user-menu-wrapper">
                                        <NavDropdown
                                            title={
                                                <div className="user-menu-button">
                                                    {picLoading &&
                                                    stableLoginType ===
                                                        "msal" ? (
                                                        <Spinner
                                                            animation="border"
                                                            size="sm"
                                                            className="user-avatar-spinner"
                                                        />
                                                    ) : profilePic &&
                                                      stableLoginType ===
                                                          "msal" ? (
                                                        <Image
                                                            src={profilePic}
                                                            roundedCircle
                                                            className="user-avatar-image"
                                                            alt={userName}
                                                        />
                                                    ) : (
                                                        <div className="user-avatar-initials">
                                                            {getInitials(
                                                                userName
                                                            )}
                                                        </div>
                                                    )}
                                                    <i className="bi bi-chevron-down dropdown-indicator"></i>
                                                </div>
                                            }
                                            id="user-dropdown"
                                            align="end"
                                        >
                                            <NavDropdown.Item
                                                as={Link}
                                                href="/profile"
                                                className="dropdown-item"
                                            >
                                                <i className="bi bi-person-circle me-2"></i>
                                                Profile
                                            </NavDropdown.Item>

                                            <NavDropdown.Item
                                                as={Link}
                                                href="/preferences?fluid=false"
                                                className="dropdown-item"
                                            >
                                                <i className="bi bi-gear me-2"></i>
                                                Preferences
                                            </NavDropdown.Item>

                                            <NavDropdown.Divider />
                                            <NavDropdown.Item
                                                onClick={handleLogoutClick}
                                                className="logout-item"
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                    ) {
                                                        e.preventDefault();
                                                        handleLogoutClick();
                                                    }
                                                }}
                                            >
                                                <i className="bi bi-box-arrow-right me-2"></i>
                                                Logout
                                            </NavDropdown.Item>
                                        </NavDropdown>
                                    </div>
                                ) : (
                                    <div className="login-wrapper">
                                        {/* Login options can be added here if needed */}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Container>
            </Navbar>

            {/* Logout Confirmation Modal */}
            <LogoutConfirmationModal
                show={showLogoutModal}
                onConfirm={handleLogoutConfirm}
                onCancel={handleLogoutCancel}
            />
        </>
    );
};

export default Header;
